/**
 * Creates a debounced version of a function that delays invocation
 * until `delay` ms have elapsed since the last call.
 *
 * The returned function exposes `.cancel()` and `.flush()` for
 * teardown and immediate execution respectively.
 */
export default function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number,
): T & { cancel: () => void; flush: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let lastArgs: Parameters<T> | null = null

  const debounced = (...args: Parameters<T>) => {
    lastArgs = args
    if (timeoutId !== null) clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      timeoutId = null
      lastArgs = null
      fn(...args)
    }, delay)
  }

  debounced.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
      timeoutId = null
      lastArgs = null
    }
  }

  debounced.flush = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
      timeoutId = null
      if (lastArgs !== null) {
        const args = lastArgs
        lastArgs = null
        fn(...args)
      }
    }
  }

  return debounced as T & { cancel: () => void; flush: () => void }
}
